import type {
  HotelAvailabilityTrackingProperties,
  HotelShopAvailabilityTrackingProperties,
  HotelShopTrackingProperties,
} from "@b2bportal/core-tracking";

export enum PackageCommonTrackingEventsName {
  // Packages app loaded
  package_entry = "package_entry",
  // Packages search viewed
  package_viewed_search = "package_viewed_search",
}

export enum PackageHotelTrackingEvents {
  // When results are loaded in the hotel availability page
  package_hotel_loaded_list_page = "package_hotel_loaded_list_page",
  // When pagination is updated in the hotel availability page
  package_hotel_updated_list_page = "package_hotel_updated_list_page",
  // Shop page loaded hotel details correctly
  package_hotel_viewed_cover = "package_hotel_viewed_cover",
}

export enum PackageFlightTrackingEvents {
  package_selected_flight_outbound = "package_selected_flight_outbound",
  package_selected_flight_return = "package_selected_flight_return",
}

export enum PackageCheckoutTrackingEvents {
  package_review_details = "package_review_details",
}

export const PACKAGE_PRODUCT_TYPE = "package" as const;

export type PackageHTSEventBasicProperties = {
  productType: typeof PACKAGE_PRODUCT_TYPE;
  package_type?: string;
};

type EncryptedProperties = {
  encryptedProperties?: string[];
};

export type PackageHTSEventProperties<Properties> =
  PackageHTSEventBasicProperties & Properties;

export const PackageEventName = {
  ...PackageCommonTrackingEventsName,
  ...PackageHotelTrackingEvents,
  ...PackageFlightTrackingEvents,
  ...PackageCheckoutTrackingEvents,
};
// eslint-disable-next-line no-redeclare -- Intentional declaration merging for value and type
export type PackageEventName = typeof PackageEventName;

export type PackageEntry = {
  name: PackageCommonTrackingEventsName.package_entry;
  properties: PackageHTSEventProperties<{
    entry: string;
    // Properties from HotelEntryEvent
    number_of_guests?: number;
    rooms_searched?: number;
    pets_searched?: number;
    market?: string;
    country?: string;
    number_of_properties?: number;
    hotel_advance?: string;
    rewards_account?: string;
    preferences_applied?: boolean;
    landing_screen?: string;
    google_place_id?: string;
    // Additional properties for PackageEntryEvent
    fare_class?: string;
    non_stop?: boolean;
    los?: string;
    number_of_adults?: number;
    number_of_children?: number;
    number_of_infants?: number;
    total_travelers?: number;
  }>;
} & EncryptedProperties;

export type PackageViewedSearch = {
  name: PackageCommonTrackingEventsName.package_viewed_search;
  properties: PackageHTSEventProperties<Record<string, unknown>>;
} & EncryptedProperties;

export type PackageHotelListLoaded = {
  name: PackageEventName["package_hotel_loaded_list_page"];
  properties: PackageHTSEventProperties<
    HotelAvailabilityTrackingProperties["properties"] & {
      page_index: number;
      page_property_count: number;
    }
  >;
} & EncryptedProperties;

export type PackageHotelUpdatedListPage = {
  name: PackageEventName["package_hotel_updated_list_page"];
  properties: PackageHTSEventProperties<
    HotelAvailabilityTrackingProperties["properties"] & {
      page_index: number;
      page_property_count: number;
    }
  >;
} & EncryptedProperties;

export type PackageHotelCoverViewed = {
  name: PackageEventName["package_hotel_viewed_cover"];
  properties: PackageHTSEventProperties<
    HotelShopTrackingProperties["properties"] &
      HotelShopAvailabilityTrackingProperties["properties"]
  >;
} & EncryptedProperties;

export type PackageFlightMetadataTrackingProperties = {
  fare_class?: string;
  city_pair?: string;
  trip_type?: string;
};

export type PackageSearchTrackingProperties = {
  origin?: string;
  destination?: string;
  adults_count?: number;
  children_count?: number;
  infants_count?: number;
  trip_type?: string;
  los?: number;
  advance?: number;
};

export type PackageLodgingTrackingProperties = {
  lodging_id?: string;
  lodging_name?: string;
  city_name?: string;
  country_name?: string;
};

export type PackageSelectedFlightOutbound = {
  name: PackageEventName["package_selected_flight_outbound"];
  properties: PackageHTSEventProperties<
    PackageFlightMetadataTrackingProperties &
      PackageSearchTrackingProperties & {
        flow_type?: string;
      }
  >;
} & EncryptedProperties;

export type PackageSelectedFlightReturn = {
  name: PackageEventName["package_selected_flight_return"];
  properties: PackageHTSEventProperties<
    PackageFlightMetadataTrackingProperties &
      PackageSearchTrackingProperties & {
        flow_type?: string;
      }
  >;
} & EncryptedProperties;

export type PackageReviewDetails = {
  name: PackageEventName["package_review_details"];
  properties: PackageHTSEventProperties<
    PackageFlightMetadataTrackingProperties &
      PackageSearchTrackingProperties &
      PackageLodgingTrackingProperties & {
        flow_type?: string;
      }
  >;
} & EncryptedProperties;

export type PackageHTSEvent =
  | PackageEntry
  | PackageViewedSearch
  | PackageHotelListLoaded
  | PackageHotelUpdatedListPage
  | PackageHotelCoverViewed
  | PackageSelectedFlightOutbound
  | PackageSelectedFlightReturn
  | PackageReviewDetails;
